import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Switch,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Pagination,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  People as AdminIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import GarageAdminsModal from './GarageAdminsModal';
import GarageSettingsModal from './GarageSettingsModal';

const GarageManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Utility function to combine address fields
  const getFormattedAddress = (garage) => {
    const addressParts = [];
    if (garage.address_line1) addressParts.push(garage.address_line1);
    if (garage.address_line2) addressParts.push(garage.address_line2);
    if (garage.town) addressParts.push(garage.town);
    return addressParts.length > 0 ? addressParts.join(', ') : 'No address';
  };
  
  // State
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searched, setSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [adminsModalOpen, setAdminsModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [loadingToggle, setLoadingToggle] = useState(null);

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearch = async () => {
    setCurrentPage(1);
    await fetchGarages(1);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const fetchGarages = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        limit: pageSize,
        offset: (page - 1) * pageSize
      };
      
      // Add search query if provided
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await apiService.searchGaragesByQuery(searchQuery.trim(), params);
      
      // Handle paginated response from backend
      const garages = response.data.garages || [];
      setGarages(garages);
      setTotalCount(response.data.total_count || 0);
      setTotalPages(Math.ceil((response.data.total_count || 0) / pageSize));
      setSearched(true);
    } catch (error) {
      console.error('Error searching garages:', error);
      
      let errorMessage = 'Failed to search garages. Please try again.';
      
      if (error.response) {
        if (error.response.status === 403) {
          errorMessage = 'Access denied. Super user permissions required.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please login again.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      setGarages([]);
      setSearched(true);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async (garageId, currentStatus) => {
    try {
      setLoadingToggle(garageId);
      
      // Add a small delay to ensure loading state is visible
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await apiService.updateGarage(garageId, { 
        is_active: !currentStatus 
      });
      
      // Update local state
      setGarages(prevGarages => 
        prevGarages.map(garage => 
          garage.id === garageId 
            ? { ...garage, is_active: !currentStatus }
            : garage
        )
      );
      
      setSuccess(`Garage ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating garage status:', error);
      setError(error.response?.data?.detail || 'Failed to update garage status');
    } finally {
      setLoadingToggle(null);
    }
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    fetchGarages(page);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    // Use updated pageSize for the API call
    fetchGaragesWithPageSize(1, newPageSize);
  };

  const fetchGaragesWithPageSize = async (page = 1, customPageSize = null) => {
    const currentPageSize = customPageSize || pageSize;
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        limit: currentPageSize,
        offset: (page - 1) * currentPageSize
      };
      
      // Add search query if provided
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      
      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await apiService.searchGaragesByQuery(searchQuery.trim(), params);
      
      // Handle paginated response from backend
      const garages = response.data.garages || [];
      setGarages(garages);
      setTotalCount(response.data.total_count || 0);
      setTotalPages(Math.ceil((response.data.total_count || 0) / currentPageSize));
      setSearched(true);
    } catch (error) {
      console.error('Error searching garages:', error);
      
      let errorMessage = 'Failed to search garages. Please try again.';
      
      if (error.response) {
        if (error.response.status === 403) {
          errorMessage = 'Access denied. Super user permissions required.';
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication required. Please login again.';
        } else if (error.response.data?.detail) {
          errorMessage = error.response.data.detail;
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      setGarages([]);
      setSearched(true);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminsClick = (garage) => {
    setSelectedGarage(garage);
    setAdminsModalOpen(true);
  };

  const handleSettingsClick = (garage) => {
    setSelectedGarage(garage);
    setSettingsModalOpen(true);
  };

  const renderMobileCard = (garage) => (
    <Card key={garage.id} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <BusinessIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {garage.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {getFormattedAddress(garage)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {garage.postcode || 'No postcode'}
            </Typography>
          </Box>
          <Chip
            icon={garage.is_active ? <ActiveIcon /> : <InactiveIcon />}
            label={garage.is_active ? 'Active' : 'Inactive'}
            color={garage.is_active ? 'success' : 'error'}
            size="small"
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AdminIcon />}
            onClick={() => handleAdminsClick(garage)}
          >
            Admins
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => handleSettingsClick(garage)}
          >
            Settings
          </Button>
          {loadingToggle === garage.id ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            <Switch
              checked={garage.is_active}
              onChange={() => handleStatusToggle(garage.id, garage.is_active)}
              color="primary"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const renderDesktopTable = () => (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Garage Name</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {garages.map((garage) => (
            <TableRow key={garage.id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2">{garage.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {garage.email}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {getFormattedAddress(garage)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {garage.postcode || 'No postcode'}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    icon={garage.is_active ? <ActiveIcon /> : <InactiveIcon />}
                    label={garage.is_active ? 'Active' : 'Inactive'}
                    color={garage.is_active ? 'success' : 'error'}
                    size="small"
                  />
                  {loadingToggle === garage.id ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    <Switch
                      checked={garage.is_active}
                      onChange={() => handleStatusToggle(garage.id, garage.is_active)}
                      color="primary"
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Manage Admins">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AdminIcon />}
                      onClick={() => handleAdminsClick(garage)}
                    >
                      Admins
                    </Button>
                  </Tooltip>
                  <Tooltip title="Garage Settings">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<SettingsIcon />}
                      onClick={() => handleSettingsClick(garage)}
                    >
                      Settings
                    </Button>
                  </Tooltip>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      {/* Alerts */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      )}

      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      )}

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Garage Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Search for garages by name or postcode
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 3 : 2,
            alignItems: isMobile ? 'stretch' : 'end'
          }}>
            <Box sx={{ flex: isMobile ? 'none' : '1', minWidth: { xs: 250, md: 300 } }}>
              <TextField
                fullWidth
                label="Search garages"
                placeholder="Enter garage name or postcode"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: 150 } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => handleStatusChange(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: { xs: '100%', md: 120 } }}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? null : <SearchIcon />}
                sx={{ 
                  height: 56
                }}
              >
{loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Search'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {/* No Search Yet */}
      {!loading && !searched && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Search for Garages
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter a garage name, postcode, or select a status filter to get started
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* No Results */}
      {!loading && searched && (!garages || garages.length === 0) && (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No garages found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or filters
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {!loading && searched && garages && garages.length > 0 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="subtitle1">
                Found {totalCount} garage{totalCount === 1 ? '' : 's'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Per page</InputLabel>
                  <Select
                    value={pageSize}
                    label="Per page"
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Page {currentPage} of {totalPages}
                </Typography>
              </Box>
            </Box>
            
            {isMobile ? (
              <Box>
                {garages.map(renderMobileCard)}
              </Box>
            ) : (
              renderDesktopTable()
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  size={isMobile ? 'small' : 'medium'}
                />
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <GarageAdminsModal
        open={adminsModalOpen}
        onClose={() => setAdminsModalOpen(false)}
        garage={selectedGarage}
      />
      
      <GarageSettingsModal
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        garage={selectedGarage}
      />
    </Box>
  );
};

export default GarageManagement;